import React, { useState, useRef, useEffect } from 'react';

import { cn } from '@easydatasolution/config-tailwind/utils';
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselPrevious,
	CarouselNext,
	CarouselDots,
} from '@easydatasolution/ui-react/components/ui/carousel';
import { FaStar } from '@react-icons/all-files/fa/FaStar';
import { StaticImage } from 'gatsby-plugin-image';

import { MarkdownRaw } from './MarkdownRaw';
interface ReviewsCarouselProps {
	reviews: Array<{
		name: string;
		text: string;
		rating: number;
		url: string;
		avatarUrl?: string;
	}>;
	className?: string;
}

export function ReviewsCarousel(props: ReviewsCarouselProps) {
	const { reviews, className } = props;
	return (
		<Carousel
			opts={{
				align: 'start',
				loop: true,
				startIndex: 3,
			}}
			className={cn('w-full', className)}
		>
			<CarouselContent className="py-1">
				{reviews.map(({ name, text, url, avatarUrl, rating }) => (
					<CarouselItem key={url} className="md:basis-1/2 lg:basis-1/3">
						<article className="mx-1 flex flex-col gap-5 rounded-lg p-8 shadow-lg">
							<header className="flex items-center">
								<div className="h-10 w-10 overflow-hidden rounded-full">
									{avatarUrl ? (
										<img
											src={avatarUrl}
											alt={`Bild von ${name}`}
											className="object-cover"
											loading="lazy"
											width="40px"
											height="40px"
										/>
									) : (
										<StaticImage
											src="../../static/images/default-avatar.jpg"
											alt={`Default Avatar`}
											className="object-cover"
											loading="lazy"
										/>
									)}
								</div>
								<div className="ml-5 flex flex-col">
									<p className="text-lg font-bold text-secondary-700">{name}</p>
									<a href={url} target="_blank" rel="noreferrer" className="text-sm text-gray-500">
										Google Rezension
									</a>
								</div>
								<div className="ml-auto flex gap-2">
									<span>{rating}.0</span>
									<FaStar className="h-5 w-5 text-yellow-300" />
								</div>
							</header>
							<ReviewText text={text} />
						</article>
					</CarouselItem>
				))}
			</CarouselContent>
			<CarouselPrevious />
			<CarouselNext />
			<CarouselDots label={(index) => `Testimonial ${index + 1}`} />
		</Carousel>
	);
}

interface ReviewTextProps {
	text: string;
}

function ReviewText({ text }: ReviewTextProps) {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isTruncated, setIsTruncated] = useState(false);
	const textRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const checkTruncation = () => {
			if (textRef.current) {
				const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight);
				const maxHeight = lineHeight * 5; // Changed from 8 to 5 to match line-clamp-5
				setIsTruncated(textRef.current.scrollHeight > maxHeight);
			}
		};

		checkTruncation();
		window.addEventListener('resize', checkTruncation);
		return () => window.removeEventListener('resize', checkTruncation);
	}, [text]);

	return (
		<div>
			<div
				ref={textRef}
				className={`prose overflow-hidden transition-all duration-300 ${
					isExpanded ? '' : 'line-clamp-5'
				}`}
			>
				<MarkdownRaw className="text-secondary-700">{text}</MarkdownRaw>
			</div>
			{isTruncated && (
				<button
					onClick={() => setIsExpanded(!isExpanded)}
					className="mt-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
				>
					{isExpanded ? 'weniger lesen' : 'mehr lesen'}
				</button>
			)}
		</div>
	);
}
